import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';
import { useAdminAgentSourceHealth } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminSourceEnvironmentHealth,
    type AdminSourceHealthEntry,
} from '@libraries/typescript/api_client/schema_types';

const SOURCE_LABELS = [
    { source: 'postgres', label: 'Postgres' },
    { source: 'datadog', label: 'Datadog' },
    { source: 'posthog', label: 'PostHog' },
    { source: 'logfire', label: 'Logfire' },
    { source: 'braintrust', label: 'Braintrust' },
    { source: 'core_api', label: 'Core API' },
] as const;

type SourceEntry = AdminSourceEnvironmentHealth;
type SourceHealth = AdminSourceHealthEntry;
type LiveStatus = SourceHealth['live_status'];

function SourceDotList({ entry }: { readonly entry: SourceEntry }) {
    const bySource = new Map(
        entry.sources.map((source) => [source.source, source]),
    );
    return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {SOURCE_LABELS.map(({ source, label }) => {
                const health = bySource.get(source);
                return (
                    <span
                        key={source}
                        className={cn(
                            'flex items-center gap-1.5 text-xs',
                            textColor(health?.live_status),
                        )}
                    >
                        <span
                            className={cn(
                                'size-1.5 rounded-full',
                                dotColor(health?.live_status),
                            )}
                        />
                        {label}
                    </span>
                );
            })}
        </span>
    );
}

function SourceDetails({ entry }: { readonly entry: SourceEntry }) {
    const bySource = new Map(
        entry.sources.map((source) => [source.source, source]),
    );
    return (
        <div className="divide-y divide-[var(--aa-border)]">
            {SOURCE_LABELS.map(({ source, label }) => {
                const health = bySource.get(source);
                return (
                    <SourceDetailRow
                        key={source}
                        label={label}
                        status={health?.live_status}
                    >
                        {statusLabel(health)}
                    </SourceDetailRow>
                );
            })}
        </div>
    );
}

function SourceDetailRow({
    label,
    status,
    children,
}: {
    readonly label: string;
    readonly status: LiveStatus | undefined;
    readonly children: string;
}) {
    return (
        <div className={cn('py-2 text-xs leading-5', textColor(status))}>
            <div className="flex items-start gap-2">
                <span
                    className={cn(
                        'mt-1.5 size-1.5 shrink-0 rounded-full',
                        dotColor(status),
                    )}
                />
                <div className="min-w-0">
                    <span className="font-semibold text-[var(--aa-ink)]">
                        {label}
                    </span>
                    <span className="text-[var(--aa-ink-faint)]"> - </span>
                    <span>{children}</span>
                </div>
            </div>
        </div>
    );
}

type SourceHealthChipsProps = {
    readonly variant?: 'chips' | 'panel';
};

export function SourceHealthChips({
    variant = 'chips',
}: SourceHealthChipsProps) {
    const { data } = useAdminAgentSourceHealth();
    if (!data) {
        return null;
    }

    if (variant === 'panel') {
        return (
            <div className="space-y-3">
                {data.environments.map((entry) => (
                    <div key={entry.environment}>
                        <div className="aa-label">{entry.environment}</div>
                        <div className="mt-1">
                            <SourceDotList entry={entry} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const production = data.environments.find(
        (entry) => entry.environment === 'production',
    );
    if (!production) {
        return null;
    }
    const summary = sourceHealthSummary(production);

    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    className="inline-flex h-8 items-center gap-2 rounded-[2px] border border-[var(--aa-border)] bg-white px-2.5 text-[12px] font-medium text-[var(--aa-ink-soft)] transition-[background-color,border-color,color] duration-100 ease-out hover:border-[var(--aa-border-strong)] hover:bg-[var(--aa-surface)] focus-visible:ring-[3px] focus-visible:ring-[var(--aa-focus)]/20 focus-visible:outline-none"
                    aria-label={`Source health: ${summary.label}`}
                >
                    <span
                        className={cn(
                            'size-1.5 rounded-full',
                            summary.dotClassName,
                        )}
                    />
                    <span>Sources</span>
                    <span className={summary.textClassName}>
                        {summary.label}
                    </span>
                </button>
            </TooltipTrigger>
            <TooltipContent
                side="bottom"
                sideOffset={10}
                hideArrow={true}
                className="admin-agent-page w-[min(900px,calc(100vw-32px))] max-w-[900px] rounded-[8px] border border-[var(--aa-border-strong)] bg-white p-0 text-left text-[var(--aa-ink)] shadow-[0_16px_40px_rgba(0,0,0,0.12)]"
            >
                <div className="border-b border-[var(--aa-border)] px-4 py-3">
                    <div className="aa-title">Live investigation sources</div>
                    <div className="mt-1 text-xs text-[var(--aa-ink-muted)]">
                        Production, staging, and local source health.
                    </div>
                </div>
                <div
                    className="grid max-h-[min(76vh,720px)] overflow-y-auto"
                    style={{
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(250px, 1fr))',
                    }}
                >
                    {data.environments.map((entry) => (
                        <div
                            key={entry.environment}
                            className="border-l border-[var(--aa-border)] px-4 py-3 first:border-l-0"
                        >
                            <div className="aa-label mb-1">
                                {entry.environment}
                            </div>
                            <SourceDetails entry={entry} />
                        </div>
                    ))}
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

function sourceHealthSummary(entry: SourceEntry): {
    readonly label: string;
    readonly dotClassName: string;
    readonly textClassName: string;
} {
    const bySource = new Map(
        entry.sources.map((source) => [source.source, source]),
    );
    const statuses = SOURCE_LABELS.map(
        ({ source }) => bySource.get(source)?.live_status,
    );
    const hardIssueCount = statuses.filter(isSourceHealthIssue).length;
    const warningCount = statuses.filter(
        (status) => status === 'expires_soon',
    ).length;
    const uncheckedCount = statuses.filter(
        (status) => status === undefined,
    ).length;
    if (hardIssueCount === 0 && warningCount === 0 && uncheckedCount === 0) {
        return {
            label: `${statuses.length}/${statuses.length}`,
            dotClassName: 'bg-[var(--aa-success)]',
            textClassName: 'text-[var(--aa-ink-muted)]',
        };
    }
    const summaryParts = [
        formatSourceHealthCount(hardIssueCount, 'issue'),
        formatSourceHealthCount(warningCount, 'warning'),
        uncheckedCount > 0 ? `${uncheckedCount} unchecked` : null,
    ].filter((part): part is string => part !== null);
    if (hardIssueCount > 0) {
        return {
            label: summaryParts.join(', '),
            dotClassName: 'bg-[var(--aa-danger)]',
            textClassName: 'text-[var(--aa-danger)]',
        };
    }
    if (warningCount > 0) {
        return {
            label: summaryParts.join(', '),
            dotClassName: 'bg-[var(--aa-warning)]',
            textClassName: 'text-[var(--aa-warning)]',
        };
    }
    return {
        label: `${uncheckedCount} unchecked`,
        dotClassName: 'bg-[var(--aa-ink-faint)]',
        textClassName: 'text-[var(--aa-ink-faint)]',
    };
}

function isSourceHealthIssue(status: LiveStatus | undefined): boolean {
    return status === 'disconnected' || status === 'reauth_required';
}

function formatSourceHealthCount(count: number, label: string): string | null {
    if (count === 0) {
        return null;
    }
    return `${count} ${label}${count === 1 ? '' : 's'}`;
}

function statusLabel(health: SourceHealth | undefined): string {
    if (!health) {
        return 'not checked';
    }
    if (health.live_status === 'connected') {
        return `connected, checked ${formatCheckedAt(health.last_checked_at)}`;
    }
    if (health.live_status === 'expires_soon') {
        return ['expires soon', health.error_message]
            .filter(Boolean)
            .join(': ');
    }
    if (health.live_status === 'reauth_required') {
        return ['reauth required', health.error_message]
            .filter(Boolean)
            .join(': ');
    }
    return [health.failure_category ?? 'unavailable', health.error_message]
        .filter(Boolean)
        .join(': ');
}

function formatCheckedAt(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    });
}

function textColor(status: LiveStatus | undefined): string {
    if (status === 'connected') {
        return 'text-[var(--aa-ink-muted)]';
    }
    if (status === 'expires_soon') {
        return 'text-[var(--aa-warning)]';
    }
    if (status === 'disconnected' || status === 'reauth_required') {
        return 'text-[var(--aa-danger)]';
    }
    return 'text-[var(--aa-ink-faint)]';
}

function dotColor(status: LiveStatus | undefined): string {
    if (status === 'connected') {
        return 'bg-[var(--aa-success)]';
    }
    if (status === 'expires_soon') {
        return 'bg-[var(--aa-warning)]';
    }
    if (status === 'disconnected' || status === 'reauth_required') {
        return 'bg-[var(--aa-danger)]';
    }
    return 'bg-[var(--aa-ink-faint)]';
}
